'use client';

import * as React from 'react';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ui/components/ui/sheet';
import { useIsMobile } from '@ui/hooks/useIsMobile';
import { cn } from '@ui/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export interface ResponsivePopoverProps {
  /**
   * Whether the popover/sheet is open
   */
  open: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * The trigger element
   */
  trigger: React.ReactNode;
  /**
   * The content to display inside the popover/sheet
   */
  children: React.ReactNode;
  /**
   * Title shown in the mobile sheet header
   */
  title?: string;
  /**
   * Popover content className (desktop only)
   */
  contentClassName?: string;
  /**
   * Popover alignment (desktop only)
   * @default "start"
   */
  align?: 'start' | 'center' | 'end';
}

/**
 * A responsive popover that shows as a Popover on desktop and a bottom Sheet on mobile.
 * Follows the same pattern as ResponsiveDialog.
 */
export const ResponsivePopover = ({
  open,
  onOpenChange,
  trigger,
  children,
  title,
  contentClassName,
  align = 'start'
}: ResponsivePopoverProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <div style={{ display: 'contents' }} onClick={() => onOpenChange(true)}>
          {trigger}
        </div>
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent side="bottom" className="flex flex-col gap-2 rounded-t-xl pt-2">
            <div className="flex justify-center mb-2">
              <div className="h-1 w-12 bg-border rounded-xl" />
            </div>
            <SheetHeader>
              {title ? (
                <SheetTitle className="text-left">{title}</SheetTitle>
              ) : (
                <SheetTitle className="sr-only">Filter options</SheetTitle>
              )}
              <SheetDescription className="sr-only">Select a filter option</SheetDescription>
            </SheetHeader>
            <div className="px-2 pb-4">{children}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={cn(contentClassName)} align={align}>
        {children}
      </PopoverContent>
    </Popover>
  );
};
